import React, { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import UserPanel from './UserPanel';

const LeftSidebar = ({
  activeSection,
  onSectionChange,
  isCollapsed,
  onToggleCollapse,
  darkMode = false,
  user = null,
  onLogout,
  onOpenChangeLogs,
  onOpenUserManagement,
  onResetPassword
}) => {
  const [userPanelOpen, setUserPanelOpen] = useState(false);

  // Stage templates are per-account now — every BDA keeps their own copies and
  // their own signature — so the tab belongs to everyone who is signed in.
  const canEditTemplates = !!user;

  const menuItems = [
    { id: 'pre sales', label: 'Pre Sales', shortLabel: 'Pre Sales', icon: 'home' },
    { id: 'post sales', label: 'Post Sales', shortLabel: 'Post Sales', icon: 'briefcase' },
    { id: 'potential clients', label: 'Potential Clients', shortLabel: 'Potential', icon: 'users' },
    { id: 'pipeline clients', label: 'Pipeline Clients', shortLabel: 'Pipeline', icon: 'trending-up' },
    { id: 'onboarded clients', label: 'Onboarded Clients', shortLabel: 'Onboarded', icon: 'check-circle' },
    { id: 'leads', label: 'Leads', shortLabel: 'Leads', icon: 'target' },
    { id: 'follow-ups', label: 'Follow-ups', shortLabel: 'Follow-ups', icon: 'clock' },
    { id: 'meetings', label: 'Meeting Schedule', shortLabel: 'Meetings', icon: 'calendar' },
    { id: 'send emails', label: 'Send Emails', shortLabel: 'Emails', icon: 'mail' },
    ...(canEditTemplates ? [{ id: 'templates', label: 'Templates', shortLabel: 'Templates', icon: 'file-text' }] : []),
    { id: 'email log', label: 'Email History', shortLabel: 'History', icon: 'history' },
    { id: 'analytics', label: 'Analytics', shortLabel: 'Analytics', icon: 'bar-chart' },
    { id: 'lead gen agent', label: 'Lead Gen Agent', shortLabel: 'Lead Gen', icon: 'sparkles' },
    { id: 'activity', label: 'Activity', shortLabel: 'Activity', icon: 'activity' },
    { id: 'highlights', label: 'Highlights', shortLabel: 'Highlights', icon: 'star' },
    { id: 'note pad', label: 'Note Pad', shortLabel: 'Notes', icon: 'clipboard', externalUrl: 'https://excalidraw.com' },
    { id: 'ai assistant', label: 'AI Assistant', shortLabel: 'AI', icon: 'sparkles' },
  ];

  const iconSvg = (iconName) => {
    const cls = 'w-5 h-5 flex-shrink-0';
    switch (iconName) {
      case 'home':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>;
      case 'briefcase':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" /><path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" /></svg>;
      case 'users':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" /></svg>;
      case 'check-circle':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;
      case 'trending-up':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" /></svg>;
      case 'calendar':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a1 1 0 011 1v1h6V3a1 1 0 112 0v1h1a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zm11 6H3v8h14V8z" clipRule="evenodd" /></svg>;
      case 'history':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8zm6-3a1 1 0 011 1v1.586l.707.707a1 1 0 01-1.414 1.414l-1-1A1 1 0 017 8V6a1 1 0 011-1z" clipRule="evenodd" /></svg>;
      case 'clock':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>;
      case 'bar-chart':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" /></svg>;
      case 'activity':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 6H12z" clipRule="evenodd" /></svg>;
      case 'star':
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>;
      case 'mail':
        return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
      case 'file-text':
        return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
      case 'sparkles':
        return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;
      case 'clipboard':
        return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>;
      case 'target':
        return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" strokeWidth={2} /><circle cx="12" cy="12" r="5" strokeWidth={2} /><circle cx="12" cy="12" r="1.5" strokeWidth={2} /></svg>;
      default:
        return <svg className={cls} fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>;
    }
  };

  const isActive = (item) => activeSection === item.id;
  const userInitial = (user?.name || 'U').charAt(0).toUpperCase();
  const userRole = user?.role === 'admin' ? 'Admin' : 'BDE';

  return (
    <>
      <div
        className={`${isCollapsed ? 'w-20' : 'w-56'} transition-all duration-300 ease-in-out ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-r flex flex-col h-full relative flex-shrink-0`}
      >
        {/* Logo */}
        <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'} flex justify-center`}>
          <img src="/leftpanel.png" alt="American Green Solutions" className="h-10 w-auto object-contain" onError={(e) => { e.target.onerror = null; e.target.src = '/logo.jpg'; }} />
        </div>

        {/* Collapse button */}
        <button
          type="button"
          onClick={onToggleCollapse}
          className={`absolute -right-3 top-16 w-6 h-6 rounded-full flex items-center justify-center shadow z-10 ${
            darkMode ? 'bg-gray-700 border border-gray-600 text-gray-300' : 'bg-white border border-gray-200 text-gray-500'
          } hover:opacity-80`}
        >
          <svg className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Nav items */}
        <nav className="flex-1 py-1 overflow-y-auto overflow-x-hidden">
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (item.externalUrl) {
                  window.open(item.externalUrl, '_blank');
                  return;
                }
                onSectionChange(item.id);
              }}
              className={`w-full flex ${isCollapsed ? 'flex-col items-center py-2.5 px-2' : 'flex-row items-center px-4 py-2.5 gap-3'} transition-all duration-200 ${
                isActive(item)
                  ? 'bg-[#679632]/10 border-l-4 border-[#679632] text-[#679632]'
                  : `border-l-4 border-transparent ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`
              }`}
            >
              <span className={isActive(item) ? 'text-[#679632]' : darkMode ? 'text-gray-400' : 'text-gray-500'}>
                {iconSvg(item.icon)}
              </span>
              {!isCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
              {isCollapsed && <span className="text-[10px] font-medium text-center leading-tight mt-0.5 truncate w-full">{item.shortLabel}</span>}
            </button>
          ))}
        </nav>

        {/* User profile at bottom — click opens right-side panel */}
        <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={() => setUserPanelOpen(true)}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center py-3 px-2' : 'gap-3 px-4 py-3'} ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
            } transition-colors`}
          >
            <div className="w-8 h-8 rounded-full bg-[#679632] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {userInitial}
            </div>
            {!isCollapsed && (
              <div className="flex-1 text-left min-w-0">
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{userRole}</p>
              </div>
            )}
            {!isCollapsed && <ChevronUp size={16} className="text-gray-400 rotate-90" />}
          </button>
        </div>
      </div>

      {/* Right-side user panel */}
      <UserPanel
        isOpen={userPanelOpen}
        onClose={() => setUserPanelOpen(false)}
        user={user}
        onLogout={onLogout}
        onOpenChangeLogs={onOpenChangeLogs}
        onOpenUserManagement={onOpenUserManagement}
        onResetPassword={onResetPassword}
      />
    </>
  );
};

export default LeftSidebar;
